import { useCallback, useEffect, useRef, useState } from 'react';
import { Modal, Table, Tag, Descriptions, Image } from 'antd';
import { fetchLoginHistory } from './services';
import moment from 'moment';
import SmartphoneIcon from '@material-ui/icons/Smartphone';
import ComputerIcon from '@material-ui/icons/Computer';
import LanguageIcon from '@material-ui/icons/Language';

const PAGE_SIZE = 10;

const iconStyle = { fontSize: 14, marginRight: 4, verticalAlign: 'middle' };

function DeviceIcon({ ua = '' }) {
  const s = ua.toLowerCase();
  if (s.includes('mobile') || s.includes('android') || s.includes('iphone'))
    return <SmartphoneIcon style={iconStyle} />;
  if (s.includes('windows') || s.includes('mac') || s.includes('linux'))
    return <ComputerIcon style={iconStyle} />;
  return <LanguageIcon style={iconStyle} />;
}

const columns = [
  {
    title: 'STT',
    key: 'index',
    width: 52,
    render: (_, __, idx) => idx + 1
  },
  {
    title: 'Thời gian',
    dataIndex: 'time',
    key: 'time',
    width: 155,
    render: v => (v ? moment(v).format('DD/MM/YYYY HH:mm:ss') : '—')
  },
  {
    title: 'Hình thức',
    dataIndex: 'login_type',
    key: 'login_type',
    width: 110,
    render: v => <Tag>{v || '—'}</Tag>
  },
  {
    title: 'Thiết bị / Trình duyệt',
    dataIndex: 'device',
    key: 'device',
    ellipsis: true,
    render: v =>
      v ? (
        <span>
          <DeviceIcon ua={v} />
          {v}
        </span>
      ) : (
        '—'
      )
  },
  {
    title: 'IP',
    dataIndex: 'ip_address',
    key: 'ip_address',
    width: 130,
    render: v => v || '—'
  }
];

function LoginDetailModal({ record, onClose }) {
  if (!record) return null;

  return (
    <Modal
      open={!!record}
      onCancel={onClose}
      title="Chi tiết đăng nhập"
      footer={null}
      width={520}
      style={{ maxWidth: '95vw' }}
      destroyOnClose>
      <Descriptions column={1} bordered size="small" style={{ marginTop: 8 }}>
        <Descriptions.Item label="Thời gian">
          {record.time ? moment(record.time).format('DD/MM/YYYY HH:mm:ss') : '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Hình thức">
          <Tag>{record.login_type || '—'}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Thiết bị / Trình duyệt">
          {record.device ? (
            <span>
              <DeviceIcon ua={record.device} />
              {record.device}
            </span>
          ) : (
            '—'
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Địa chỉ IP">
          {record.ip_address || '—'}
        </Descriptions.Item>
        {record.os && (
          <Descriptions.Item label="Hệ điều hành">
            {record.os}
          </Descriptions.Item>
        )}
        {record.browser && (
          <Descriptions.Item label="Trình duyệt">
            {record.browser}
          </Descriptions.Item>
        )}
        {record.location && (
          <Descriptions.Item label="Vị trí">
            {record.location}
          </Descriptions.Item>
        )}
        {record.face_url && (
          <Descriptions.Item label="Ảnh khuôn mặt">
            <Image
              src={record.face_url}
              width={120}
              style={{ borderRadius: 8, border: '1px solid #e5e7eb' }}
              alt="Face"
            />
          </Descriptions.Item>
        )}
      </Descriptions>
    </Modal>
  );
}

export default function LoginHistoryModal({ open, onClose }) {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const pageRef = useRef(1);

  const load = useCallback(async (pageId) => {
    setLoading(true);
    try {
      const res = await fetchLoginHistory({ perPage: PAGE_SIZE, pageId });
      if (res) {
        const d = res.data?.data;
        setData(d?.login_histories || d?.data || []);
        setTotal(d?.total || 0);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      pageRef.current = 1;
      setPage(1);
      load(1);
    }
  }, [open, load]);

  const handlePageChange = (p) => {
    pageRef.current = p;
    setPage(p);
    load(p);
  };

  return (
    <>
      <Modal
        open={open}
        onCancel={onClose}
        title="Lịch sử đăng nhập"
        footer={null}
        width={820}
        style={{ maxWidth: '95vw' }}
        destroyOnClose>
        <Table
          rowKey={(r, i) => r.id ?? i}
          columns={columns}
          dataSource={data}
          loading={loading}
          pagination={{
            current: page,
            pageSize: PAGE_SIZE,
            total,
            onChange: handlePageChange,
            showSizeChanger: false,
            showTotal: (t) => `Tổng ${t} bản ghi`
          }}
          size="small"
          scroll={{ x: 580 }}
          onRow={(record) => ({
            onClick: () => setSelectedRecord(record),
            style: { cursor: 'pointer' }
          })}
          rowClassName={() => 'login-history-row'}
        />
      </Modal>

      <LoginDetailModal
        record={selectedRecord}
        onClose={() => setSelectedRecord(null)}
      />
    </>
  );
}
